import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import {
    Card,
    CardMedia,
    Typography,
    Box,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import { Bookmark, BookmarkBorder, Star } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../context/AuthContext';
import { addToWishlist, removeFromWishlist } from '../services/api';

// import markerIcon from 'leaflet/dist/images/marker-icon.png';
// import markerShadow from 'leaflet/dist/images/marker-shadow.png';


// Create two different sized icons for regular and wishlisted listings
const createIcon = (size) =>
    L.icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
        iconSize: [size, size * 1.64],
        iconAnchor: [size / 2, size * 1.64],
        popupAnchor: [1, -(size * 1.64 - 8)],
        tooltipAnchor: [16, -28],
        shadowSize: [size * 1.64, size * 1.64],
    });

const regularIcon = createIcon(25);
const wishlistedIcon = createIcon(35);

// Updated popup card styling (mimicking ListingCard)
const StyledPopupCard = styled(Card)(({ theme }) => ({
    width: '160px',
    transition: 'transform 0.15s ease-in-out',
    '&:hover': { transform: 'scale3d(1.02, 1.02, 1)' },
    borderRadius: '8px',
    position: 'relative',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.04)',
    overflow: 'hidden',
    cursor: 'pointer',
}));

// Updated media styling (square aspect ratio)
const StyledCardMedia = styled(CardMedia)({
    aspectRatio: '1/1',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
});

// Updated Bookmark IconButton styling
const BookmarkIconButton = styled(IconButton)(({ theme, active }) => ({
    position: 'absolute',
    top: '10px',
    right: '10px',
    color: active ? '#3AB961' : '#FFFFFF',
    padding: '6px',
    zIndex: 1,
    '& .MuiSvgIcon-root': {
        fontSize: '20px',
    },
}));

// Updated content wrapper styling; reduced height since location text is removed
const ContentWrapper = styled(Box)(({ theme }) => ({
    padding: '11px',
    display: 'flex',
    flexDirection: 'column',
    height: '60px',
    justifyContent: 'space-between',
}));

// Rating badge styling remains the same
const RatingBadge = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: '6px',
    left: '11px',
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#212121',
    borderRadius: '14px',
    padding: '4px 8px',
    justifyContent: 'center',
}));

const StyledLeafletPopup = styled(Popup)({
    '& .leaflet-popup-content-wrapper': {
        padding: 0,
        overflow: 'hidden',
        borderRadius: '8px',
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
    '& .leaflet-popup-content': {
        margin: 0,
        width: '160px !important',
    },
    '& .leaflet-popup-close-button': {
        display: 'none',
    },
    '& .leaflet-popup-tip': {
        backgroundColor: '#fff',
    },
});

// Utility function to format ratings (integer if whole, one decimal otherwise)
const formatRating = (rating) => {
    const parsedRating = parseFloat(rating);
    if (!isNaN(parsedRating)) {
        return parsedRating % 1 === 0 ? parsedRating.toFixed(0) : parsedRating.toFixed(1);
    }
    return '5';
};

const ListingsMapView = ({ listings, wishlistedItems, onWishlistUpdate }) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });
    const [wishlistLoading, setWishlistLoading] = React.useState(false);

    const handleWishlistToggle = async (e, listingId) => {
        e.preventDefault();
        e.stopPropagation();

        if (!currentUser) {
            setSnackbar({
                open: true,
                message: 'Please log in to add items to your wishlist',
                severity: 'warning'
            });
            return;
        }

        if (wishlistLoading) return;

        setWishlistLoading(true);
        try {
            if (wishlistedItems.has(listingId)) {
                await removeFromWishlist(listingId);
            } else {
                await addToWishlist(listingId);
            }
            onWishlistUpdate(listingId, !wishlistedItems.has(listingId));
        } catch (error) {
            console.error('Error updating wishlist:', error);
            setSnackbar({
                open: true,
                message: 'Failed to update wishlist. Please try again.',
                severity: 'error'
            });
        } finally {
            setWishlistLoading(false);
        }
    };

    const getMapCenter = () => {
        if (listings.length === 0) return [40.7128, -74.0060];
        const lats = listings.map(listing => listing.locations[0]?.latitude).filter(Boolean);
        const lngs = listings.map(listing => listing.locations[0]?.longitude).filter(Boolean);

        if (lats.length === 0 || lngs.length === 0) return [40.7128, -74.0060];

        return [
            lats.reduce((a, b) => a + b) / lats.length,
            lngs.reduce((a, b) => a + b) / lngs.length
        ];
    };

    return (
        <Box sx={{ height: '70vh', width: '100%', mt: 2 }}>
            <MapContainer
                center={getMapCenter()}
                zoom={13}
                style={{ height: '100%', width: '100%', borderRadius: '8px' }}
            >
                <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
                {listings.map(listing =>
                    listing.locations?.[0] && (
                        <Marker
                            key={listing.uuid}
                            position={[listing.locations[0].latitude, listing.locations[0].longitude]}
                            icon={wishlistedItems.has(listing.id) ? wishlistedIcon : regularIcon}
                        >
                            <StyledLeafletPopup>
                                <StyledPopupCard onClick={() => navigate(`/listings/${listing.uuid}`)}>
                                    {listing.images?.[0] && (
                                        <Box position="relative">
                                            <StyledCardMedia
                                                component="img"
                                                image={listing.images[0].image_url}
                                                alt={listing.title}
                                            />
                                            <BookmarkIconButton
                                                active={wishlistedItems.has(listing.id) ? 1 : 0}
                                                onClick={(e) => handleWishlistToggle(e, listing.id)}
                                                size="small"
                                                disabled={wishlistLoading}
                                            >
                                                {wishlistedItems.has(listing.id) ? <Bookmark /> : <BookmarkBorder />}
                                            </BookmarkIconButton>
                                            {Number(listing.average_rating) > 0 && (
                                                <RatingBadge>
                                                    <Star sx={{ fontSize: '12px', color: '#3AB961' }} />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            marginLeft: '2px',
                                                            fontSize: '11px',
                                                            color: '#F6F8FA',
                                                            fontWeight: 400,
                                                        }}
                                                    >
                                                        {formatRating(listing.average_rating)}
                                                    </Typography>
                                                </RatingBadge>
                                            )}
                                        </Box>
                                    )}
                                    <ContentWrapper>
                                        <Typography
                                            variant="subtitle1"
                                            component="h3"
                                            sx={{
                                                fontSize: '13px',
                                                lineHeight: '16px',
                                                fontWeight: 500,
                                                marginBottom: '4px',
                                                height: '32px',
                                                overflow: 'hidden',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                            }}
                                        >
                                            {listing.title || 'Untitled Listing'}
                                        </Typography>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontSize: '11.5px',
                                                fontFamily: 'Filson Soft, sans-serif',
                                                fontWeight: 500,
                                                color: '#34A853',
                                                '& span': {
                                                    fontSize: '12.5px',
                                                    fontWeight: 500,
                                                },
                                            }}
                                        >
                                            ${typeof listing.price === 'number' ? listing.price.toFixed(2) : listing.price}
                                            <span>/day</span>
                                        </Typography>
                                    </ContentWrapper>
                                </StyledPopupCard>
                            </StyledLeafletPopup>
                        </Marker>
                    )
                )}
            </MapContainer>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ListingsMapView;
